// src/components/dashboard/CardCheckoutAsaas.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Heading,
    Text,
    VStack,
    HStack,
    FormControl,
    FormLabel,
    Input,
    Select,
    InputGroup,
    InputLeftElement,
    useToast,
} from '@chakra-ui/react';
import { FiCreditCard, FiAlertCircle } from 'react-icons/fi';
import { supabase } from '../../config/supabaseClient';
import InputMask from 'react-input-mask';

const CardCheckoutAsaas = ({ userId, selectedPackage, validCoupon, couponCode, packages, onSuccess, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [cardError, setCardError] = useState('');
    const toast = useToast();
    const [formData, setFormData] = useState({
        cardNumber: '',
        cardHolderName: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        installments: 1
    });
    const [formIsValid, setFormIsValid] = useState(false);

    useEffect(() => {
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                toast({
                    title: "Sessão expirada",
                    description: "Sua sessão expirou. Por favor, faça login novamente.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
                // Redirecionar para a página de login
                window.location.href = '/login';
            }
        };

        checkSession();
    }, [toast]);

    // Validar formulário quando os campos mudam
    useEffect(() => {
        const { cardNumber, cardHolderName, expiryMonth, expiryYear, cvv } = formData;
        const isValid =
            cardNumber.replace(/\D/g, '').length === 16 &&
            cardHolderName.trim().length >= 3 &&
            expiryMonth !== '' &&
            expiryYear !== '' &&
            cvv.length >= 3;

        setFormIsValid(isValid);
    }, [formData]);

    const selectedPkg = packages.find(pkg => pkg.id === selectedPackage);

    // Calcular preço com desconto
    const calculatePrice = () => {
        if (!selectedPkg) return 0;
        const basePrice = selectedPkg.price;
        return validCoupon ? basePrice * 0.85 : basePrice; // 15% de desconto
    };

    // Lidar com mudanças nos campos do formulário
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Lidar com envio do formulário
    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setCardError('');

        /*
        console.log('Iniciando pagamento, dados:', {
            amount: calculatePrice(),
            userId,
            selectedPackage,
            credits: selectedPkg.credits,
            couponCode: validCoupon ? couponCode : null
        });
        */

        try {
            // Verificar sessão atual
            const { data: { session } } = await supabase.auth.getSession();
            //console.log('Sessão atual:', session ? 'Autenticado' : 'Não autenticado');

            if (!session) {
                toast({
                    title: 'Sessão expirada',
                    description: 'Sua sessão expirou. Por favor, faça login novamente.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });

                // Redirecionar para login
                window.location.href = '/login';
                return;
            }

            const authToken = session.access_token;
            //console.log('Token de autenticação:', authToken ? `${authToken.substring(0, 10)}...` : 'Não encontrado');

            // MUDANÇA 1: Obter dados do perfil do usuário ANTES de criar o pagamento
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('name, email, phone, cpf_cnpj, postal_code, address_number, complement')
                .eq('id', userId)
                .single();

            if (profileError) {
                console.error('Erro ao obter dados do perfil:', profileError);
                throw new Error('Erro ao obter dados do perfil');
            }

            if (!profileData.cpf_cnpj) {
                throw new Error('É necessário cadastrar seu CPF/CNPJ para realizar pagamentos com cartão');
            }

            // Agora primeiro cria o pagamento com os dados do cliente já disponíveis
            const customerResponse = await fetch('https://mlliabdepyirxtaytcco.functions.supabase.co/create-asaas-payment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    amount: calculatePrice(),
                    currency: 'brl',
                    package_id: selectedPackage,
                    credits: selectedPkg.credits,
                    coupon_code: validCoupon ? couponCode : null,
                    coupon_discount: validCoupon ? 15 : 0,
                    customer: {
                        name: profileData.name || 'Cliente',
                        email: profileData.email,
                        phone: profileData.phone || '',
                        cpfCnpj: profileData.cpf_cnpj.replace(/\D/g, '')
                    }
                }),
            });

            if (!customerResponse.ok) {
                const errorData = await customerResponse.json();
                throw new Error(errorData.error || 'Erro ao criar pagamento');
            }

            const paymentData = await customerResponse.json();
            //console.log('Resposta da API (create-asaas-payment):', paymentData);

            if (!paymentData.id) {
                throw new Error('ID de pagamento não recebido da API');
            }

            // Processar pagamento com cartão no ASAAS
            const { cardNumber, cardHolderName, expiryMonth, expiryYear, cvv, installments } = formData;

            // Processar pagamento com cartão no endpoint do cartão
            const cardResponse = await fetch('https://mlliabdepyirxtaytcco.functions.supabase.co/process-asaas-credit-card', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    paymentId: paymentData.id,
                    cardData: {
                        holderName: cardHolderName,
                        number: cardNumber.replace(/\D/g, ''),
                        expiryMonth,
                        expiryYear,
                        cvv
                    },
                    holderInfo: {
                        name: profileData.name || cardHolderName,
                        email: profileData.email,
                        cpfCnpj: profileData.cpf_cnpj.replace(/\D/g, ''),
                        postalCode: profileData.postal_code ? profileData.postal_code.replace(/\D/g, '') : '00000000',
                        addressNumber: profileData.address_number || '100',
                        addressComplement: profileData.complement || '',
                        phone: profileData.phone || ''
                    },
                    installmentCount: parseInt(installments)
                }),
            });

            if (!cardResponse.ok) {
                const errorData = await cardResponse.json();
                throw new Error(errorData.error || 'Erro ao processar pagamento com cartão');
            }

            const cardResult = await cardResponse.json();
            //console.log('Resposta do processamento do cartão:', cardResult);

            if (cardResult.status === 'CONFIRMED' || cardResult.status === 'RECEIVED') {
                // Pagamento bem-sucedido
                toast({
                    title: 'Compra realizada com sucesso!',
                    description: `${selectedPkg.credits} créditos foram adicionados à sua conta.`,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });

                // Notificar componente pai
                if (onSuccess) onSuccess();

                // Fechar modal
                onClose();
            } else if (cardResult.status === 'PENDING') {
                // Pagamento em processamento
                toast({
                    title: 'Pagamento em processamento',
                    description: 'Seu pagamento está sendo processado. Você receberá um e-mail quando for confirmado.',
                    status: 'info',
                    duration: 5000,
                    isClosable: true,
                });

                // Notificar componente pai
                if (onSuccess) onSuccess();

                // Fechar modal
                onClose();
            } else {
                // Erro no pagamento
                throw new Error(`Pagamento não confirmado. Status: ${cardResult.status}`);
            }
        } catch (error) {
            console.error('Erro ao processar pagamento:', error);

            setCardError(error.message || 'Ocorreu um erro ao processar seu pagamento.');

            toast({
                title: 'Erro no processamento',
                description: error.message || 'Ocorreu um erro ao processar seu pagamento.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    // Gerar opções de parcelas
    const installmentOptions = () => {
        const maxInstallments = 12;
        const price = calculatePrice();
        const minInstallmentValue = 5; // Valor mínimo de parcela

        let options = [];
        for (let i = 1; i <= maxInstallments; i++) {
            const installmentValue = price / i;
            if (installmentValue < minInstallmentValue) break;

            const interestRate = i > 1 ? 0.015 * (i - 1) : 0; // 1.5% ao mês a partir da segunda parcela
            const installmentValueWithInterest = i === 1
                ? installmentValue
                : installmentValue * (1 + interestRate);

            const label = i === 1
                ? `À vista - R$ ${installmentValue.toFixed(2)}`
                : `${i}x de R$ ${installmentValueWithInterest.toFixed(2)} ${interestRate > 0 ? '(com juros)' : '(sem juros)'}`;

            options.push(
                <option key={i} value={i}>
                    {label}
                </option>
            );
        }

        return options;
    };

    // Gerar anos para select
    const yearOptions = () => {
        const currentYear = new Date().getFullYear();
        let options = [];

        for (let i = 0; i < 15; i++) {
            const year = currentYear + i;
            options.push(
                <option key={year} value={year}>
                    {year}
                </option>
            );
        }

        return options;
    };

    return (
        <form onSubmit={handleSubmit}>
            <VStack spacing={6}>
                {/* Resumo da compra */}
                <Box p={4} bg="gray.50" borderRadius="md" width="100%">
                    <Heading size="md" mb={2} color="gray.700">
                        Resumo
                    </Heading>

                    <HStack justify="space-between" mb={2}>
                        <Text>Pacote:</Text>
                        <Text fontWeight="medium">{selectedPkg?.description}</Text>
                    </HStack>

                    <HStack justify="space-between" mb={2}>
                        <Text>Valor original:</Text>
                        <Text>R$ {selectedPkg?.price.toFixed(2)}</Text>
                    </HStack>

                    {validCoupon && (
                        <HStack justify="space-between" mb={2}>
                            <Text>Desconto (15%):</Text>
                            <Text color="green.500">-R$ {(selectedPkg?.price * 0.15).toFixed(2)}</Text>
                        </HStack>
                    )}

                    <HStack justify="space-between" mt={3} pt={3} borderTopWidth="1px" borderColor="gray.200">
                        <Text fontWeight="bold">Total:</Text>
                        <Text fontWeight="bold" color="brand.primary" fontSize="xl">
                            R$ {calculatePrice().toFixed(2)}
                        </Text>
                    </HStack>
                </Box>

                {/* Formulário de cartão de crédito */}
                <Box p={4} borderWidth="1px" borderRadius="md" width="100%">
                    <Heading size="sm" mb={4}>
                        Dados do Cartão de Crédito
                    </Heading>

                    <VStack spacing={4}>
                        <FormControl isRequired>
                            <FormLabel>Número do Cartão</FormLabel>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none">
                                    <FiCreditCard color="gray.300" />
                                </InputLeftElement>
                                <Input
                                    as={InputMask}
                                    mask="9999 9999 9999 9999"
                                    maskChar={null}
                                    name="cardNumber"
                                    value={formData.cardNumber}
                                    onChange={handleInputChange}
                                    placeholder="0000 0000 0000 0000"
                                />
                            </InputGroup>
                        </FormControl>

                        <FormControl isRequired>
                            <FormLabel>Nome no Cartão</FormLabel>
                            <Input
                                name="cardHolderName"
                                value={formData.cardHolderName}
                                onChange={handleInputChange}
                                placeholder="Como está gravado no cartão"
                            />
                        </FormControl>

                        <HStack spacing={4} width="100%">
                            <FormControl isRequired>
                                <FormLabel>Mês</FormLabel>
                                <Select
                                    name="expiryMonth"
                                    value={formData.expiryMonth}
                                    onChange={handleInputChange}
                                    placeholder="Mês"
                                >
                                    <option value="01">01</option>
                                    <option value="02">02</option>
                                    <option value="03">03</option>
                                    <option value="04">04</option>
                                    <option value="05">05</option>
                                    <option value="06">06</option>
                                    <option value="07">07</option>
                                    <option value="08">08</option>
                                    <option value="09">09</option>
                                    <option value="10">10</option>
                                    <option value="11">11</option>
                                    <option value="12">12</option>
                                </Select>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Ano</FormLabel>
                                <Select
                                    name="expiryYear"
                                    value={formData.expiryYear}
                                    onChange={handleInputChange}
                                    placeholder="Ano"
                                >
                                    {yearOptions()}
                                </Select>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>CVV</FormLabel>
                                <Input
                                    name="cvv"
                                    value={formData.cvv}
                                    onChange={handleInputChange}
                                    placeholder="000"
                                    maxLength={4}
                                />
                            </FormControl>
                        </HStack>


                        {/* Formulário para Parcelas - Mas precisa ajustar obackend ou envio
                        <FormControl>
                            <FormLabel>Parcelas</FormLabel>
                            <Select
                                name="installments"
                                value={formData.installments}
                                onChange={handleInputChange}
                            >
                                {installmentOptions()}
                            </Select>
                        </FormControl>
                        */}

                        {cardError && (
                            <Box p={3} bg="red.50" borderRadius="md" width="100%">
                                <HStack>
                                    <FiAlertCircle color="red" />
                                    <Text color="red.500" fontSize="sm">
                                        {cardError}
                                    </Text>
                                </HStack>
                            </Box>
                        )}
                    </VStack>
                </Box>

                <Button
                    type="submit"
                    colorScheme={formIsValid ? "blue" : "gray"}
                    bgGradient={formIsValid
                        ? "linear(to-r, brand.primary, brand.secondary)"
                        : "linear(to-r, gray.300, gray.400)"}
                    color="white"
                    size="lg"
                    width="full"
                    isLoading={loading}
                    loadingText="Processando pagamento..."
                    disabled={!formIsValid || loading}
                    opacity={formIsValid ? 1 : 0.7}
                    _hover={formIsValid
                        ? {
                            transform: 'translateY(-2px)',
                            boxShadow: 'md',
                        }
                        : {}
                    }
                    _disabled={{
                        cursor: 'not-allowed',
                    }}
                    position="relative"
                    transition="all 0.3s ease"
                >
                    {!formIsValid ? (
                        <>
                            <Box
                                position="absolute"
                                top="0"
                                left="0"
                                right="0"
                                bottom="0"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bg="blackAlpha.200"
                                borderRadius="md"
                            >
                                <HStack spacing={2}>
                                    <FiAlertCircle />
                                    <Text>Preencha todos os campos</Text>
                                </HStack>
                            </Box>
                            <Text opacity={0}>Finalizar compra</Text>
                        </>
                    ) : (
                        "Finalizar compra"
                    )}
                </Button>

                {/* Texto explicativo abaixo do botão */}
                {!formIsValid && (
                    <Text
                        fontSize="sm"
                        color="gray.500"
                        textAlign="center"
                        mt={2}
                    >
                        Preencha todos os campos obrigatórios do cartão para habilitar o botão
                    </Text>
                )}
            </VStack>
        </form>
    );
};

export default CardCheckoutAsaas;